import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Button, Label, Switch } from '../../../../Components/uielements';
import DropDown from '../../../../Components/uielements/dropDown';

import {
  ModalButtonWrapper,
  ModalGap,
  OutlineButton,
  SwitcherLabel,
} from '../../../../services/settings/appStyle/global.style';

import { IntlMessages } from '../../../../helper/langHelper';

import { CheckBoxContainer, ContentWrapper, ModalWrapperNew } from '../user.style';

import CheckBoxField from '../../../../Components/form-fields/checkBox';

import {
  assignPermissionToUser,
  assignPermissionToUserAsDistributor,
  getPermissionList,
} from '../../../../services/bioplus-services/user-permission';

import { useUserContext } from '../../../../hooks/UserContext';

import {
  PermissionRowWrapper,
  PermissionLabel,
  PermissionActions,
  PermissionAction,
  RangeFieldWrapper,
  PermissionNumberField,
  PermissionNumberLabel,
  PermissionNumberInput,
  TooltipText,
} from '../../SubscriptionPlanV2/Subscription.style';

import { isLimitApplicable, isRangeApplicable } from '../../SubscriptionPlanV2/helper';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const ROLES_WITHOUT_ACCESS_TOGGLE = ['ADMIN', 'PARTNER', 'OWNER', 'VISITOR'];

// Roles (of the person performing the edit) that are allowed to set
// Range/Limit values on an OWNER's permissions. 'PARTNER' is the internal
// role string used for Distributor accounts in this codebase.
const ROLES_ALLOWED_TO_EDIT_OWNER_LIMITS = ['ADMIN', 'PARTNER'];

const ACCESS_OPTIONS = [
  {
    label: 'Mobile',
    value: 'M',
  },
  {
    label: 'Desktop',
    value: 'D',
  },
  {
    label: 'Both',
    value: 'B',
  },
];

const isPermissionToggleable = (permission) => {
  if (!permission) return false;
  return (
    permission.isView !== null ||
    permission.isCreate !== null ||
    permission.isManage !== null ||
    permission.isReport !== null
  );
};

const PermissionNumberInputField = ({ label, value, onChange, disabled }) => {
  const handleChange = (e) => {
    const nextValue = e.target.value;

    if (nextValue === '') {
      onChange({
        target: {
          value: '',
        },
      });
      return;
    }

    if (Number(nextValue) < 0) {
      onChange({
        target: {
          value: '0',
        },
      });
      return;
    }

    onChange({
      target: {
        value: nextValue,
      },
    });
  };

  const handleKeyDown = (e) => {
    if (['-', '+', 'e', 'E'].includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <PermissionNumberField>
      <PermissionNumberLabel>{label}</PermissionNumberLabel>

      <PermissionNumberInput
        type="number"
        min="0"
        step="1"
        value={value ?? ''}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onWheel={(e) => e.currentTarget.blur()}
        disabled={disabled}
      />
    </PermissionNumberField>
  );
};

const PermissionRow = ({
  item,
  index,
  onToggle,
  onRangeChange,
  onLimitChange,
  roleName,
  currentUserRole,
  disabled,
}) => {
  const toggleable = isPermissionToggleable(item);
  if (!toggleable) return null;

  const rangeApplicable = isRangeApplicable(item.label);
  const limitApplicable = isLimitApplicable(item.label);

  const isAdminOrDistributor = [
    'ADMIN',
    // 'PARTNER'
  ].includes(roleName);

  const isOwner = roleName === 'OWNER';

  // Only ADMIN and DISTRIBUTOR (acting user) are allowed to modify an
  // OWNER's Range/Limit values (premise, geofence, eDEC). Every other
  // acting role keeps the existing OWNER behaviour (toggles only).
  const isActingUserAdminOrDistributor =
    ROLES_ALLOWED_TO_EDIT_OWNER_LIMITS.includes(currentUserRole);

  const canEditOwnerLimits = isOwner && isActingUserAdminOrDistributor;

  /*
   * ADMIN / DISTRIBUTOR:
   * No permission switches displayed.
   *
   * Other roles:
   * View, Create, Manage, Report displayed.
   *
   * ADMIN / DISTRIBUTOR only:
   * Range / Limit shown where applicable.
   * All other roles: Range / Limit hidden.
   *
   * OWNER (additional):
   * When the acting user is ADMIN or DISTRIBUTOR, Range / Limit are also
   * shown alongside the usual toggles, where applicable (premise/geofence
   * range, eDEC limit only). No other acting role can edit an OWNER's
   * Range / Limit.
   */
  const showPermissionActions = !isAdminOrDistributor;

  const showRange =
    (isAdminOrDistributor && rangeApplicable) || (canEditOwnerLimits && rangeApplicable);

  const showLimit =
    (isAdminOrDistributor && limitApplicable) || (canEditOwnerLimits && limitApplicable);

  return (
    <>
      {item.description && (
        <TooltipText style={{ display: 'flex', gap: '5px' }}>
          <InfoOutlinedIcon /> {item.description}
        </TooltipText>
      )}
      <PermissionRowWrapper>
        <PermissionLabel>{item.label}</PermissionLabel>

        {showPermissionActions && (
          <PermissionActions>
            {['isView', 'isCreate', 'isManage', 'isReport'].map((field) => (
              <PermissionAction key={field}>
                <Switch
                  handleToggle={() => onToggle(index, field)}
                  checked={!!item[field]}
                  disabled={disabled}
                />

                <SwitcherLabel>
                  {field === 'isView' && (
                    <IntlMessages id="text.feature.view" defaultValue="View" />
                  )}

                  {field === 'isCreate' &&
                    (item.label === 'user' ? (
                      <IntlMessages id="text.feature.invite" defaultValue="Invite" />
                    ) : (
                      <IntlMessages id="text.feature.create" defaultValue="Create" />
                    ))}

                  {field === 'isManage' && (
                    <IntlMessages id="text.feature.manager" defaultValue="Manage" />
                  )}

                  {field === 'isReport' && (
                    <IntlMessages id="text.feature.report" defaultValue="Report" />
                  )}
                </SwitcherLabel>
              </PermissionAction>
            ))}
          </PermissionActions>
        )}

        {showRange && (
          <RangeFieldWrapper>
            <PermissionNumberInputField
              label={
                item.label?.toLowerCase() === 'geofence' ? 'Range (Hectare)' : 'Range (Dia in Km)'
              }
              value={item.range}
              onChange={(e) => onRangeChange(index, e.target.value)}
              disabled={disabled}
            />
          </RangeFieldWrapper>
        )}

        {showLimit && (
          <RangeFieldWrapper>
            <PermissionNumberInputField
              label="Limit"
              value={item.limit ?? 0}
              onChange={(e) => onLimitChange(index, e.target.value)}
              disabled={disabled}
            />
          </RangeFieldWrapper>
        )}
      </PermissionRowWrapper>
    </>
  );
};

const PermissionFormV2 = ({ onClose, userData, isPartner, invalidateQuery }) => {
  const [permission, setPermission] = useState([]);
  const [assignAll, setAssignAll] = useState(false);

  const [originalPermissions, setOriginalPermissions] = useState([]);

  const [roleAccess, setRoleAccess] = useState(userData?.roleAccess || 'BOTH');

  const userContext = useUserContext();

  const userRole = userContext.company;

  const companyId = userRole.companyId?.id;

  const userId = userData?.id;

  const partnerId = userData?.partnerId;

  const roleId = userData?._id;

  const queryClient = useQueryClient();

  /*
   * Current logged-in user's role.
   */
  const currentUserRole = (userRole?.role || userRole?.roleName || '').toString().toUpperCase();

  /*
   * Member whose permissions are being edited.
   */
  const targetRole = (userData?.role || userData?.roleName || '').toString().toUpperCase();

  /*
   * Try to identify the current logged-in user.
   * Adjust this if your UserContext uses a
   * different ID structure.
   */
  const currentUserId = userContext?.user?.id || userContext?.user?._id || userContext?.id;

  const targetUserId = userData?.userId?._id || userData?.userId?.id || userData?.id;

  /*
   * Member cannot update their own permissions.
   */
  const isOwnPermission =
    currentUserId && targetUserId && String(currentUserId) === String(targetUserId);

  /*
   * ADMIN and DISTRIBUTOR:
   * Can update any user.
   *
   * OWNER:
   * Can update company users,
   * but cannot update themselves.
   */
  const canUpdatePermissions =
    ['ADMIN', 'PARTNER', 'EMPLOYEE'].includes(currentUserRole) ||
    (currentUserRole === 'OWNER' && !isOwnPermission);

  const isPermissionDisabled = !canUpdatePermissions;

  const isAccessDropdownDisabled =
    ROLES_WITHOUT_ACCESS_TOGGLE.includes(targetRole) || isPermissionDisabled;

  const query = useQuery(['permission'], () =>
    isPartner ? null : getPermissionList(companyId, userId, roleId)
  );

  useEffect(() => {
    if (query?.isSuccess) {
      setPermission(query?.data?.permission || []);

      if (!isPartner) {
        setRoleAccess(query?.data?.roleAccess);
      }

      setOriginalPermissions(query?.data?.permission || []);
    }

    if (isPartner && userData) {
      setPermission(userData?.permission || []);

      setOriginalPermissions(userData?.permission || []);
    }
  }, [isPartner, query?.data, query?.isSuccess, userData]);

  useEffect(() => {
    setRoleAccess(userData?.roleAccess || 'BOTH');
  }, [userData]);

  const handleToggle = (index, field) => {
    if (isPermissionDisabled) return;

    setPermission((prevPermission) => {
      const updatedPermission = [...prevPermission];

      updatedPermission[index] = {
        ...updatedPermission[index],
        [field]: !updatedPermission[index][field],
      };

      return updatedPermission;
    });
  };

  const handleRangeChange = (index, value) => {
    if (isPermissionDisabled) return;

    setPermission((prevPermission) => {
      const updatedPermission = [...prevPermission];

      updatedPermission[index] = {
        ...updatedPermission[index],
        range: value,
      };

      return updatedPermission;
    });
  };

  const handleLimitChange = (index, value) => {
    if (isPermissionDisabled) return;

    setPermission((prevPermission) => {
      const updatedPermission = [...prevPermission];

      updatedPermission[index] = {
        ...updatedPermission[index],
        limit: value,
      };

      return updatedPermission;
    });
  };

  const handleAssignAllChange = () => {
    if (isPermissionDisabled) return;

    if (!assignAll) {
      setOriginalPermissions(permission);

      setPermission((prevPermission) =>
        prevPermission.map((item) => ({
          ...item,
          isView: true,
          isCreate: true,
          isManage: true,
          isReport: true,
        }))
      );
    } else {
      setPermission(originalPermissions);
    }

    setAssignAll(!assignAll);
  };

  const handleAccessChange = (option) => {
    if (isAccessDropdownDisabled) return;

    setRoleAccess(option?.value ?? option);
  };

  const mutation = useMutation(
    (payload) =>
      isPartner
        ? // assignPermissionToUserAsDistributor(partnerId, userData?.userId?._id, payload)
          assignPermissionToUser(companyId, userId, payload, roleId)
        : assignPermissionToUser(companyId, userId, payload, roleId),
    {
      onSuccess: async () => {
        alert('Success! Permissions assigned successfully!');

        // if (isPartner) {
        await queryClient.invalidateQueries(invalidateQuery);
        // }

        onClose();
      },

      onError: () => {
        alert('Oops! Something went wrong. Please try again later.');

        onClose();
      },
    }
  );

  const handleSubmit = () => {
    if (isPermissionDisabled) return;

    const payload = isPartner
      ? {
          permission,
          companyId: userData?.companyId,
          roleAccess,
        }
      : {
          permission,
          roleAccess,
        };

    mutation.mutate(payload);
  };

  return (
    <ModalWrapperNew>
      {query?.isLoading ? (
        <Label>Loading permissions...</Label>
      ) : (
        <>
          {isPartner && permission?.length === 0 ? (
            <Label>No permissions available.</Label>
          ) : (
            <>
              <ModalGap>
                <ContentWrapper>
                  <CheckBoxContainer>
                    <CheckBoxField
                      name="assignAll"
                      checked={assignAll}
                      onClick={handleAssignAllChange}
                      disabled={isPermissionDisabled}
                    />

                    <Label>
                      <IntlMessages id="text.assign.all.permissions" />
                    </Label>
                  </CheckBoxContainer>
                </ContentWrapper>

                <DropDown
                  selectedOption={handleAccessChange}
                  options={ACCESS_OPTIONS}
                  name="roleAccess"
                  label="Access"
                  defaultValue={ACCESS_OPTIONS.find((option) => option.value === roleAccess)?.label}
                  disabled={isAccessDropdownDisabled}
                  isDropDownNotClickable={isAccessDropdownDisabled}
                />
              </ModalGap>

              <div
                style={{
                  display: 'grid',
                  gap: '20px',
                  paddingTop: '20px',
                }}
              >
                {permission?.length > 0 &&
                  permission.map((item, index) => (
                    <PermissionRow
                      key={item.label ?? index}
                      item={item}
                      index={index}
                      roleName={targetRole}
                      currentUserRole={currentUserRole}
                      disabled={isPermissionDisabled}
                      onToggle={handleToggle}
                      onRangeChange={handleRangeChange}
                      onLimitChange={handleLimitChange}
                    />
                  ))}
              </div>

              <ModalButtonWrapper
                style={{
                  paddingTop: '20px',
                }}
              >
                <OutlineButton onClick={onClose}>
                  <IntlMessages id="modal.button.cancel" defaultValue="Cancel" />
                </OutlineButton>

                {!isPermissionDisabled && (
                  <Button varient="normal" onClick={handleSubmit} disabled={mutation?.isLoading}>
                    <IntlMessages id="modal.button.submit" defaultValue="Submit" />

                    {mutation?.isLoading ? '...' : ''}
                  </Button>
                )}
              </ModalButtonWrapper>
            </>
          )}
        </>
      )}
    </ModalWrapperNew>
  );
};

export default PermissionFormV2;
